import { Alert } from '@prisma/client';
import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@zond/ui';
import * as LucideIcons from 'lucide-react';
import { LucideProps } from 'lucide-react';
import { Activity, BookTemplate } from 'lucide-react';
import { createElement } from 'react';
import i18n from '../../../i18n/config';
import { AlertActions } from './alert-actions';

type AlertWithExecutions = Alert & {
  executions: { id: number; createdAt: Date; projectReportId: number }[];
};

export const columns: ColumnDef<AlertWithExecutions>[] = [
  {
    id: 'icon',
    accessorKey: 'icon',
    header: () => i18n.t('alerts:columns.icon'),
    enableSorting: false,
    size: 50,
    cell: ({ row }) => {
      const iconName = row.getValue('icon') as string;
      const Icon = LucideIcons[iconName as keyof typeof LucideIcons] as React.ComponentType<LucideProps>;
      return (
        <div className="w-8 h-8 flex items-center justify-center">
          {Icon && createElement(Icon, { className: 'w-5 h-5' })}
        </div>
      );
    },
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: () => i18n.t('alerts:columns.name'),
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: () => i18n.t('alerts:columns.description'),
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: () => i18n.t('alerts:columns.createdAt'),
    cell: ({ row }) => {
      const date = new Date(row.getValue('createdAt'));
      return <div>{date.toLocaleDateString()}</div>;
    },
  },
  {
    id: 'executions',
    header: () => (
      <div className="flex items-center gap-2">
        <Activity className="w-4 h-4" />
        <span>{i18n.t('alerts:columns.executions')}</span>
      </div>
    ),
    cell: ({ row }) => {
      const executions = row.original.executions || [];
      return (
        <div className="flex items-center">
          <Badge variant="secondary" className="font-mono">
            {executions.length}
          </Badge>
        </div>
      );
    },
  },
  {
    id: 'fromTemplate',
    header: () => (
      <div className="flex items-center gap-2">
        <BookTemplate className="w-4 h-4" />
        <span>{i18n.t('alerts:columns.template')}</span>
      </div>
    ),
    cell: ({ row }) => {
      const isTemplate = row.original.fromTemplate;
      return isTemplate ? (
        <Badge className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20">
          {i18n.t('alerts:badge.template')}
        </Badge>
      ) : (
        <Badge variant="outline" className="text-muted-foreground">
          {i18n.t('alerts:badge.custom')}
        </Badge>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <AlertActions alert={row.original} />
        </div>
      );
    },
  },
];

export const columnOrder = [
  'icon',
  'name',
  'description',
  'executions',
  'fromTemplate',
  'createdAt',
  'actions',
];
